import React from "react";
import { createPortal } from "react-dom";
import { Flag, Radio, ClipboardPenLine } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useTutorialStatus } from "@/queries/account";
import {
  getIntroStorageKey,
  readIntroCompleted,
  writeIntroCompleted,
  setTutorialLock,
  clearTutorialLock,
  purgeGuestTutorialStorage,
} from "@/utils/tutorialStorage";

const BACKEND_SYNC_DEFAULT = process.env.REACT_APP_TUTORIAL_SYNC !== "false";

const INTRO_POINTS = [
  {
    icon: Flag,
    title: "Följ matcher och tabell",
    description: "Se matcher, resultat och tabelläget samlat på ett ställe.",
  },
  // {
  //   icon: Radio,
  //   title: "Håll koll live",
  //   description: "Följ matcherna live, håll koll på resultat och statistik.",
  // },
  {
    icon: ClipboardPenLine,
    title: "För digitala protokoll",
    description: "Skapa, fyll i och spara dina matchprotokoll digitalt.",
  },
];

export default function IntroTutorial() {
  const { user, ready } = useAuth() || {};
  const [run, setRun] = React.useState(false);
  const [introCompleted, setIntroCompleted] = React.useState(true);
  const [introHydrated, setIntroHydrated] = React.useState(!BACKEND_SYNC_DEFAULT);

  const storageKey = React.useMemo(() => (user ? getIntroStorageKey(user) : null), [user]);
  const { data: tutorialStatusData, isLoading: tutorialStatusLoading } = useTutorialStatus({
    enabled: ready && !!user && BACKEND_SYNC_DEFAULT,
  });

  React.useEffect(() => {
    if (!ready || !user) {
      setIntroCompleted(true);
      setIntroHydrated(false);
      setRun(false);
      return;
    }
    purgeGuestTutorialStorage();
    if (!storageKey) {
      setIntroCompleted(true);
      setIntroHydrated(!BACKEND_SYNC_DEFAULT);
      setRun(false);
      return;
    }

    const localCompleted = readIntroCompleted(storageKey);
    if (localCompleted) {
      setIntroCompleted(true);
      setIntroHydrated(true);
      return;
    }

    if (BACKEND_SYNC_DEFAULT && tutorialStatusLoading) {
      setIntroHydrated(false);
      return;
    }

    const serverCompleted = Boolean(tutorialStatusData?.tutorial?.introCompleted);
    if (serverCompleted) {
      writeIntroCompleted(storageKey);
    }
    setIntroCompleted(serverCompleted);
    setIntroHydrated(true);
  }, [ready, storageKey, tutorialStatusData, tutorialStatusLoading, user]);

  React.useEffect(() => {
    if (!ready || !user || !storageKey || !introHydrated) return;
    if (introCompleted || run) return;
    setRun(true);
  }, [introCompleted, introHydrated, ready, run, storageKey, user]);

  const completeIntro = React.useCallback(() => {
    setRun(false);
    setIntroCompleted(true);
    writeIntroCompleted(storageKey);
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("speedway:intro:completed", { detail: { key: storageKey } })
      );
    }
  }, [storageKey]);

  React.useEffect(() => {
    if (!run) return undefined;
    setTutorialLock("intro");
    if (typeof document !== "undefined") {
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalOverflow;
        clearTutorialLock("intro");
      };
    }
    return () => clearTutorialLock("intro");
  }, [run]);

  if (!ready || !user || !introHydrated || !run) return null;

  const portalTarget = typeof document !== "undefined" ? document.body : null;
  if (!portalTarget) return null;

  return createPortal(
    <div className="intro-tutorial" role="dialog" aria-modal>
      <div className="intro-tutorial__background" aria-hidden />
      <div className="intro-tutorial__card intro-tutorial__card--single">
        <div className="intro-tutorial__brand">
          <img
            src="/icons/icon-512x512.png"
            alt="Lapstr"
            className="intro-tutorial__logo"
          />
        </div>

        <div className="intro-tutorial__content intro-tutorial__content--single">
          <h2 className="intro-tutorial__title">Välkommen till Lapstr</h2>
          <p className="intro-tutorial__text">
            Den självklara appen för speedway-entusiasten. Följ Bauhausligan och skapa dina egna digitala matchprotokoll – snabbt, enkelt och direkt i appen.
          </p>
        </div>

        <div className="intro-tutorial__points" aria-label="Det här kan du göra i appen">
          {INTRO_POINTS.map(({ icon: Icon, title, description }) => (
            <div key={title} className="intro-tutorial__point">
              <div className="intro-tutorial__point-icon-wrap">
                <Icon className="intro-tutorial__point-icon" aria-hidden />
              </div>
              <div className="intro-tutorial__point-copy">
                <h3 className="intro-tutorial__point-title">{title}</h3>
                <p className="intro-tutorial__point-text">{description}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="intro-tutorial__footer intro-tutorial__footer--single">
          <button type="button" className="intro-tutorial__primary" onClick={completeIntro}>
            Fortsätt
          </button>
        </div>
      </div>
    </div>,
    portalTarget
  );
}
